import { createStyles } from "@mantine/core";
import { getStylesRef } from "@mantine/styles";
import { QR, BTN_RESET } from "./theme";

// Shared look for the list / past-due / failed table-card screens (they're
// visually identical shells in the prototype: toolbar + KPI row + table card).
const useSharedStyles = createStyles(() => ({
  container: {
    width: "100%",
    backgroundColor: QR.bg,
    minHeight: "100vh",
  },
  content: {
    padding: "24px 28px 60px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
  },

  crumbBar: {
    display: "flex",
    alignItems: "center",
  },
  backBtn: {
    ...BTN_RESET,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: QR.surfaceAlt,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.line, color: QR.ink },
  },

  toolbarCard: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  btnAdd: {
    ...BTN_RESET,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    backgroundColor: QR.accent,
    color: QR.ink,
    fontWeight: 700,
    fontSize: 13.5,
    fontFamily: "Inter",
    textAlign: "center",
    "&:hover": { backgroundColor: QR.accentHover, color: QR.ink },
  },
  iconBtnSquare: {
    ...BTN_RESET,
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    color: QR.ink2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { background: QR.surfaceSoft },
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 14,
    "@media (max-width: 1080px)": { gridTemplateColumns: "1fr" },
  },
  kpi: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    position: "relative",
    cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(16,24,40,.08)",
    },
    [`&:hover .${getStylesRef("kpiArrow")}`]: {
      background: QR.accent,
      borderColor: QR.accent,
      color: QR.ink,
    },
  },
  kpiArrow: {
    ref: getStylesRef("kpiArrow"),
    position: "absolute",
    top: 14,
    right: 14,
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: `1px solid ${QR.line}`,
    display: "grid",
    placeItems: "center",
    color: QR.muted,
    background: QR.surface,
  },
  kpiTop: { display: "flex", alignItems: "center", gap: 10 },
  kpiChip: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiChipAmber: { background: QR.accentSoft, color: "#B45309" },
  kpiChipRed: { background: QR.dangerSoft, color: QR.dangerDeep },
  kpiLabel: { fontSize: 12.5, fontWeight: 600, color: QR.muted },
  kpiValue: { fontSize: 28, fontWeight: 700, letterSpacing: "-0.022em", fontFamily: "Inter" },
  kpiSub: { fontSize: 12, color: QR.muted2 },

  tableWrap: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    overflow: "hidden",
  },
  cardToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderBottom: `1px solid ${QR.lineSoft}`,
  },
  segmented: {
    display: "flex",
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 10,
    padding: 4,
    gap: 2,
  },
  segmentedBtn: {
    ...BTN_RESET,
    padding: "6px 14px",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    color: QR.muted,
    fontFamily: "Inter",
    textAlign: "center",
  },
  segmentedBtnOn: {
    background: QR.purple,
    color: "#fff",
  },
  search: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 36,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "0 12px",
    color: QR.muted2,
  },
  searchInput: {
    border: "none",
    outline: "none",
    flex: 1,
    background: "transparent",
    fontSize: 13.5,
    fontFamily: "Inter",
  },

  // Wraps tableScroll so the refresh LoadingOverlay can cover exactly the
  // rows (and block clicks on them) while their statuses are being refetched.
  tableScrollWrap: { position: "relative" },
  tableScroll: {
    overflowX: "auto",
    // fixed viewport height so filtering never collapses the layout
    minHeight: 548,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    "& thead th": {
      background: QR.tableHeadBg,
      fontSize: 12.5,
      fontWeight: 600,
      color: QR.ink,
      textAlign: "left",
      padding: "10px 16px",
      whiteSpace: "nowrap",
      fontFamily: "Inter",
    },
    "& tbody td": {
      padding: "12px 16px",
      fontSize: 14,
      borderTop: `1px solid ${QR.lineSoft}`,
      whiteSpace: "nowrap",
      fontFamily: "Inter",
    },
    "& tbody tr:hover": { background: QR.surfaceSoft },
  },
  rowClickable: { cursor: "pointer" },
  tdMain: { fontWeight: 600 },
  num: { fontVariantNumeric: "tabular-nums" },
  emptyCell: { textAlign: "center", color: QR.muted, padding: "40px 16px" },
  centeredLoader: { height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" },

  // Bottom toast (.toast in the prototype)
  toast: {
    position: "fixed",
    bottom: 26,
    left: "50%",
    transform: "translateX(-50%)",
    background: QR.purple,
    color: "#fff",
    padding: "11px 20px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 600,
    fontFamily: "Inter",
    zIndex: 2000,
    boxShadow: "0 10px 30px rgba(0,0,0,.3)",
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    textTransform: "uppercase",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".05em",
    padding: "4px 12px",
    borderRadius: 999,
    border: "1px solid",
    fontFamily: "Inter",
  },

  startBtn: {
    ...BTN_RESET,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 13px",
    borderRadius: 8,
    background: QR.accent,
    color: QR.ink,
    fontSize: 12.5,
    fontWeight: 700,
    whiteSpace: "nowrap",
    fontFamily: "Inter",
    "&:hover": { background: QR.accentHover },
  },
  kebab: {
    ...BTN_RESET,
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: QR.muted,
    "&:hover": { background: QR.surfaceAlt, color: QR.ink },
  },
  // Wraps a menu item's label text directly — guarantees the color renders
  // regardless of Menu's own internal item/itemLabel cascade.
  dangerText: { color: QR.dangerDeep },

  cardPagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: `1px solid ${QR.lineSoft}`,
  },
  pagInfo: { fontSize: 13, color: QR.muted, fontFamily: "Inter" },
  pagBtns: { display: "flex", gap: 6 },
  pageBtn: {
    ...BTN_RESET,
    minWidth: 34,
    height: 34,
    borderRadius: 8,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    fontSize: 13,
    fontWeight: 600,
    color: QR.ink2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 10px",
    "&:hover:not(.on)": { background: QR.surfaceSoft },
  },
  pageBtnOn: { background: QR.accent, borderColor: QR.accent, color: QR.ink },

  wlTitle: { fontSize: 19, fontWeight: 700, fontFamily: "Inter", letterSpacing: "-0.015em", color: QR.ink },
}));

export default useSharedStyles;
